import { FilterEnum, IFilter, LinkEnum } from 'components/Filter/SingleFilter'
import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { NavBar } from 'components/NavBar/NavBar'
import { useState } from 'react'
import { Flex, H1WithBack, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText } from 'styling/Typography'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
    PlanCode,
    TargetCell,
    ProgressTrack,
    ProgressFill,
} from '../Operations.styled'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { useNavigate } from 'react-router-dom'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import {
    activateOperationPlan,
    cancelOperationPlan,
    clearOperationPlans,
    completeOperationPlan,
    getOperationPlans,
    pauseOperationPlan,
    resumeOperationPlan,
} from 'features/operations/plansSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Pagination } from 'components/Pagination/Pagination'
import { NoData } from 'components/NoData'
import { IGetAllOperationPlansSingleResponse } from 'types/api/operationsPlansApi.interface'
import { planStatuses, planPriorities } from './plansData'
import { tokens } from 'styling/tokens'

export const Plans = () => {
    const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { data, totalPages } = useSelector((state: RootState) => state.operationPlans)
    const plans = data ?? []

    const columns: IHonestTableColumn<IGetAllOperationPlansSingleResponse>[] = [
        {
            id: 'plan',
            header: 'Plan',
            sortable: true,
            sticky: true,
            minWidth: '14rem',
            cell: (item) => (
                <Cell>
                    <BodyText weight='600'>{item.name}</BodyText>
                    <PlanCode>{item.code}</PlanCode>
                </Cell>
            ),
        },
        {
            id: 'site',
            header: 'Site',
            cell: (item) => <BodyText>{item.site?.name || '-'}</BodyText>,
        },
        {
            id: 'department',
            header: 'Department',
            cell: (item) =>
                item.departmentName ? (
                    <BodyText>{item.departmentName}</BodyText>
                ) : (
                    <OtherText grey>—</OtherText>
                ),
        },
        {
            id: 'priority',
            header: 'Priority',
            cell: (item) => <BodyText>{item.priority ? planPriorities.find((priority) => priority.value === item.priority)?.label : '-'}</BodyText>,
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => {
                const status = planStatuses.find((status) => status.value === item.status)
                return <StatusPill status={status?.tone ?? 'neutral'}>{status?.label}</StatusPill>
            },
        },
        {
            id: 'target',
            header: 'Target',
            cell: (item) => {
                const percent = item.targetValue > 0
                    ? Math.min(100, Math.max(0, Math.round(((item.currentValue ?? 0) / item.targetValue) * 100)))
                    : 0
                return (
                    <TargetCell>
                        <OtherText>
                            {(item.currentValue ?? 0).toLocaleString('en-GB')} / {item.targetValue.toLocaleString('en-GB')} {item.targetUnit?.name}
                        </OtherText>
                        {item.targetValue > 0 && (
                            <Flex center gap='0.5rem' width='100%'>
                                <ProgressTrack>
                                    <ProgressFill style={{ width: `${percent}%` }} />
                                </ProgressTrack>
                                <OtherText grey>{percent}%</OtherText>
                            </Flex>
                        )}
                    </TargetCell>
                )
            },
        },
        {
            id: 'boardVisibility',
            header: 'Board Visibility',
            align: 'right',
            cell: (item) => (
                <BodyText title='Who sees this plan on leadership dashboards'>
                    {item.boardVisibility === 1 ? 'Internal (team)' : item.boardVisibility === 2 ? 'Ops directors' : 'Executive'}
                </BodyText>
            ),
        },
        {
            id: 'queueHealth',
            header: 'Queue Health',
            cell: (item) => (
                <span title='Is the scheduler keeping enough work queued for this plan?'>
                    <StatusPill status={item.queueHealth === 1 ? 'success' : 'warning'}>
                        {item.queueHealth === 1 ? 'On track' : 'Behind'}
                    </StatusPill>
                </span>
            ),
        },
        {
            id: 'owner',
            header: 'Owner',
            align: 'center',
            cell: (item) => (
                <BodyText>{item.owner ? item.owner.firstName + ' ' + item.owner.lastName : '-'}</BodyText>
            ),
        },
        {
            id: 'lastUpdate',
            header: 'Last update',
            cell: (item) => <OtherText>{formatDateForDisplay(item.updatedAt)}</OtherText>,
        },
    ]

    useQueryPagination({ fetchFunctions: [getOperationPlans], cleanUpFunctions: [clearOperationPlans], params })

    const filters: IFilter[] = [
        {
            type: FilterEnum.SELECT,
            name: 'Status',
            options: planStatuses,
            propName: 'status',
            single: true,
        },
        {
            type: FilterEnum.ASYNC_SELECT,
            name: 'Site',
            link: LinkEnum.LOCATIONS,
            propName: 'siteNodeId',
            single: true,
        },
    ]

    const handlePlanAction = (action: typeof cancelOperationPlan) => async (item: IGetAllOperationPlansSingleResponse) => {
        await dispatch(action({ id: item.id }))
        dispatch(getOperationPlans(params))
    }

    const getOptions = (item: IGetAllOperationPlansSingleResponse): IHonestTableMenuOption<IGetAllOperationPlansSingleResponse>[] => {
        const planMenuOptions: IHonestTableMenuOption<IGetAllOperationPlansSingleResponse>[] = [
            { label: 'Duplicate', onClick: (item) => navigate(`${item.id}/duplicate`) },
            { label: 'Audit log', onClick: (item) => navigate(`${item.id}/audit`) },
        ]

        const cancelOption: IHonestTableMenuOption<IGetAllOperationPlansSingleResponse> = {
            label: 'Cancel',
            color: tokens.status.danger.fg,
            onClick: handlePlanAction(cancelOperationPlan),
        }

        if (item.status === 1) {
            planMenuOptions.push({ label: 'Activate', onClick: handlePlanAction(activateOperationPlan) })
            planMenuOptions.push(cancelOption)
            planMenuOptions.push({ label: 'Edit', onClick: (item) => navigate(`${item.id}/edit`) })
        } else if (item.status === 2) {
            planMenuOptions.push(
                { label: 'Pause', onClick: handlePlanAction(pauseOperationPlan) },
                { label: 'Complete', onClick: handlePlanAction(completeOperationPlan) },
                cancelOption,
            )
        } else if (item.status === 3) {
            planMenuOptions.push(
                { label: 'Resume', onClick: handlePlanAction(resumeOperationPlan) },
                cancelOption,
            )
        }

        return planMenuOptions
    }

    return (
        <>
            <NavBar
                enableTemporaryManualClosing
                filterOptions={filters}
                params={params}
                input
                defaultValue={params.q}
                placeholder='Search Operation Groups...'
                setParams={setParams}
                searchFields={['name']}
                limit={30}
                visibleFilterOverflow
            />
            <PageWrapper>
                <Flex between center mb='16'>
                    <Flex center gap='1rem'>
                        <H1WithBack title='Plans' arrow />
                    </Flex>
                    <Button variant='primary' onClick={() => navigate('add')} >Add</Button>
                </Flex>
                {plans.length ? <HonestTable
                    columns={columns}
                    onClick={(item) => navigate(`${item.id}`)}
                    data={plans}
                    keyField='id'
                    columnToggle
                    menuOptions={(item) => getOptions(item)}
                /> : <Flex column center justcenter gap='1rem'>
                    <NoData text='No operation plans created yet.' />
                    <Button variant='primary' onClick={() => navigate('/operations/plans/add')}>Create plan</Button>
                </Flex>}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}